import { useEffect, useState } from 'react';
import { ChevronRight, Leaf } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import type { Plant } from './types';
import './seo-enhancements.css';

type PublicPlant = Plant & { public_path: string };

function ensureRobots(content: string) {
  let meta = document.head.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'robots';
    document.head.appendChild(meta);
  }
  meta.content = content;
}

export function RouteRobotsMeta() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, '') || '/';
    const isPrivate = path === '/account' || path.startsWith('/account/') || path === '/admin' || path.startsWith('/admin/') || path === '/my-plants' || path.startsWith('/my-plants/');
    const isPublic = path === '/'
      || path === '/plants'
      || path.startsWith('/plants/')
      || path === '/propagation'
      || path.startsWith('/propagation/')
      || path === '/community'
      || path === '/pricing'
      || path === '/privacy'
      || path === '/terms'
      || path === '/community-guidelines';

    ensureRobots(isPrivate || !isPublic ? 'noindex,nofollow,noarchive' : 'index,follow,max-image-preview:large');
  }, [location.pathname]);
  return null;
}

export function RelatedPlantLinks() {
  const location = useLocation();
  const [plants, setPlants] = useState<PublicPlant[]>([]);
  const [failed, setFailed] = useState(false);
  const match = location.pathname.match(/^\/plants\/([^/]+)\/?$/);
  const token = match?.[1] || '';

  useEffect(() => {
    if (!token) {
      setPlants([]);
      return;
    }
    let active = true;
    setFailed(false);
    fetch(`/api/public/plants/${encodeURIComponent(token)}/related`)
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || 'Could not load related plants');
        return body as { plants: PublicPlant[] };
      })
      .then((result) => active && setPlants(result.plants))
      .catch(() => active && setFailed(true));
    return () => { active = false; };
  }, [token]);

  if (!token || failed || plants.length === 0) return null;

  return (
    <section className="related-plants-section" aria-labelledby="related-plants-title">
      <div className="related-plants-heading">
        <div><span>Keep exploring</span><h2 id="related-plants-title">Related plants</h2></div>
        <Link to="/plants">Browse plant library <ChevronRight size={16} /></Link>
      </div>
      <div className="related-plants-grid">
        {plants.map((plant) => (
          <Link className="related-plant-card" to={plant.public_path} key={plant.id}>
            <span className="related-plant-icon"><Leaf size={20} /></span>
            <div><strong>{plant.common_name}</strong><em>{plant.scientific_name}</em><small>{plant.light}</small></div>
            <ChevronRight size={17} />
          </Link>
        ))}
      </div>
    </section>
  );
}
